import { Col, Form, Input, Row, Select, Space, Spin, Typography, Upload, message } from 'antd';
import axios from 'axios';
import { Building, Globe, Hash, Mail, MapPin, Phone, User } from 'lucide-react';
import { useContext, useEffect, useReducer, useState } from 'react';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { IoDocumentTextOutline } from 'react-icons/io5';

import SectionCard from '../../../components/sectionCard/SectionCard';
import StepFooter from '../../../components/stepFooter/StepFooter';
import UploadTile from '../../../components/uploadTile/UploadTile';
import useStore from '../../../store/useStore';
import { commonRegExp, getObjectFromArray } from '../../../utils';
import { RegisterProviderContext } from '../RegisterProviderContext';

const { Text } = Typography;

/** Форм 2 багана: жижиг дэлгэцэнд нэг багана болж хураагдана */
const COL_SPAN = { xs: 24, md: 12 };

/** Зөвшөөрөгдөх файлын дээд хэмжээ */
const MAX_FILE_SIZE = 5_000_000;

/** Талбарын дүрсний нэг ижил өнгө, хэмжээ */
const fieldIcon = (Icon) => <Icon size={16} style={{ color: '#98a2b3' }} />;

const actions = { imageChange: 'IMAGE_CHANGE' };

const initialState = { idCardFront: null, idCardBack: null };

function reducer(state, action) {
  switch (action.type) {
    case actions.imageChange: {
      const { key, file } = action.payload;
      return { ...state, [key]: file };
    }
    default:
      return state;
  }
}

const Step1 = ({ nextStep }) => {
  const [type, setType] = useState();
  const [affiliations, setAffiliations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [autoFill] = useStore((state) => [state?.user?.autoFill]);
  const token = useStore((state) => state.token);

  const { formStep1Values, setFormStep1Values } = useContext(RegisterProviderContext) ?? {};

  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    // Хүсэлт буцаж ирэхээс өмнө алхам солигдвол төлөв шинэчлэхгүй
    let active = true;

    const fetchFields = async () => {
      setFieldsLoading(true);
      try {
        const { data } = await axios.get('/api/v1.0/merchant', {
          headers: { Authorization: token },
        });
        if (!active) return;

        const toOptions = (field) =>
          (field?.enum ?? []).map((option) => ({
            value: option,
            label: field?.enumDictionary?.[option] ?? option,
          }));

        const affiliationOptions = toOptions(getObjectFromArray(data.result, 'affiliation'));
        // Буцаж ирэхэд өмнөх сонголт нь хэвээр үлдэнэ
        if (affiliationOptions.length) {
          setType(formStep1Values?.affiliation ?? affiliationOptions[0].value);
        }

        setAffiliations(affiliationOptions);
        setCategories(toOptions(getObjectFromArray(data.result, 'category')));
      } catch (e) {
        console.error('Fetch Merchant Fields: ', e);
      } finally {
        if (active) setFieldsLoading(false);
      }
    };
    void fetchFields();

    return () => {
      active = false;
    };
    //eslint-disable-next-line
  }, []);

  const onFinish = (values) => {
    setFormStep1Values({ ...values, affiliation: type });
    nextStep();
  };

  const normFile = (e) => (Array.isArray(e) ? e : e && e.fileList);

  const isValidFileSize = (file) => file.size < MAX_FILE_SIZE;

  const beforeUpload = (file) => {
    if (!isValidFileSize(file)) {
      message.error('Файлын хэмжээ 5mb -аас бага байх ёстой!');
    }
    return isValidFileSize(file);
  };

  /** Иргэний үнэмлэх / гэрчилгээний нэг талын хавсралт */
  const attachmentItem = (name, label, requiredMessage) => (
    <Col {...COL_SPAN}>
      <Form.Item
        name={name}
        label={label}
        valuePropName="fileList"
        getValueFromEvent={normFile}
        rules={[{ required: true, message: requiredMessage }]}
      >
        <Upload
          showUploadList={false}
          beforeUpload={beforeUpload}
          maxCount={1}
          multiple={false}
          onChange={({ fileList: [{ originFileObj: file }] }) => {
            if (isValidFileSize(file)) {
              dispatch({ type: actions.imageChange, payload: { file, key: name } });
            }
          }}
        >
          <UploadTile file={state[name]} />
        </Upload>
      </Form.Item>
    </Col>
  );

  /** Бизнесийн лого — иргэн, байгууллага хоёуланд ижил */
  const brandLogoItem = (
    <Col {...COL_SPAN}>
      <Form.Item
        name="brandLogo"
        label="Бизнесийн лого"
        valuePropName="fileList"
        getValueFromEvent={normFile}
        rules={[{ required: true, message: 'Бизнесийн лого оруулна уу' }]}
      >
        <Upload listType="picture" beforeUpload={() => false} multiple={false} maxCount={1}>
          <UploadTile />
        </Upload>
      </Form.Item>
    </Col>
  );

  const isCitizen = type === 'C';

  const identityFields = (
    <>
      <Col {...COL_SPAN}>
        <Form.Item
          label={isCitizen ? 'Таны нэр' : 'Байгууллагын нэр'}
          name="name"
          rules={[
            {
              required: true,
              message: isCitizen ? 'Нэр оруулна уу' : 'Байгууллагын нэр оруулна уу',
            },
          ]}
        >
          <Input
            prefix={fieldIcon(isCitizen ? User : Building)}
            placeholder={isCitizen ? 'Таны нэр' : 'Байгууллагын нэр'}
          />
        </Form.Item>
      </Col>
      <Col {...COL_SPAN}>
        <Form.Item
          label={isCitizen ? 'Регистрийн дугаар' : 'Байгууллагын РД дугаар'}
          name="registrationNumber"
          rules={[
            {
              required: true,
              message: isCitizen
                ? 'Регистрийн дугаар оруулна уу'
                : 'Байгууллагын РД дугаар оруулна уу',
            },
            {
              pattern: new RegExp(
                isCitizen ? commonRegExp.registrationNumber : commonRegExp.orgRegistrationNumber
              ),
              message: isCitizen
                ? 'Регистрийн дугаар буруу байна.'
                : 'Байгууллагын РД дугаар буруу байна.',
            },
          ]}
        >
          <Input prefix={fieldIcon(Hash)} placeholder={isCitizen ? 'УУ12345678' : '1234567'} />
        </Form.Item>
      </Col>
    </>
  );

  return (
    <Form
      autoComplete="off"
      layout="vertical"
      onFinish={onFinish}
      initialValues={formStep1Values ?? autoFill ?? {}}
    >
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <SectionCard
          icon={<AiOutlineInfoCircle />}
          title="Ерөнхий мэдээлэл"
          /* Төрөл нь талбар биш, бүх формыг сольдог тул гарчгийн мөрөнд байрлана */
          action={
            <Space size={8}>
              <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                Бүртгэлийн төрөл
              </Text>
              <Select
                value={type}
                onChange={setType}
                options={affiliations}
                loading={fieldsLoading}
                style={{ width: 160 }}
              />
            </Space>
          }
        >
          {/* Төрөл ирэхээс өмнө талбарууд харагдахгүй тул хоосон карт биш spinner */}
          {fieldsLoading && (
            <div style={{ textAlign: 'center', padding: 32 }}>
              <Spin />
            </div>
          )}
          {/* 8 талбар — 2 багана дээр яг 4 мөр болж, хоосон нүд үлдэхгүй */}
          <Row gutter={16}>
            {type && identityFields}

            {type && brandLogoItem}

            {type && (
              <>
                <Col {...COL_SPAN}>
                  <Form.Item
                    name="category"
                    label="Үйл ажиллагааны чиглэл"
                    rules={[{ required: true, message: 'Үйл ажиллагааны чиглэл сонгоно уу' }]}
                  >
                    <Select
                      placeholder="Сонгох"
                      options={categories}
                      optionFilterProp="label"
                      loading={fieldsLoading}
                    />
                  </Form.Item>
                </Col>
                <Col {...COL_SPAN}>
                  <Form.Item
                    name="phoneNumber"
                    label="Утасны дугаар"
                    rules={[{ required: true, message: 'Утас оруулна уу' }]}
                  >
                    <Input prefix={fieldIcon(Phone)} placeholder="99999999" />
                  </Form.Item>
                </Col>
                <Col {...COL_SPAN}>
                  <Form.Item
                    name="email"
                    label="Мэйл"
                    rules={[{ required: true, message: 'Мэйл оруулна уу' }]}
                  >
                    <Input prefix={fieldIcon(Mail)} placeholder="Таны мэйл" />
                  </Form.Item>
                </Col>
                <Col {...COL_SPAN}>
                  <Form.Item name="website" label="Вэбсайт">
                    <Input prefix={fieldIcon(Globe)} placeholder="www.example.com" />
                  </Form.Item>
                </Col>
                <Col {...COL_SPAN}>
                  <Form.Item
                    name="address"
                    label="Бүртгэлтэй хаяг"
                    rules={[{ required: true, message: 'Бүртгэлтэй хаяг оруулна уу' }]}
                  >
                    <Input prefix={fieldIcon(MapPin)} placeholder="Дэлгэрэнгүй хаяг" />
                  </Form.Item>
                </Col>
              </>
            )}
          </Row>
        </SectionCard>

        {type && (
          <SectionCard
            icon={<IoDocumentTextOutline />}
            title={isCitizen ? 'Иргэний үнэмлэх' : 'Улсын бүртгэлийн гэрчилгээ'}
          >
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Зураг эсвэл PDF, нэг файл 5MB-аас бага байна.
              </Text>
              <Row gutter={16}>
                {isCitizen
                  ? attachmentItem(
                      'idCardFront',
                      'Урд тал',
                      'Иргэний үнэмлэхний урд талын зургийг оруулна уу'
                    )
                  : attachmentItem(
                      'certificateFront',
                      'Урд тал',
                      'Улсын бүртгэлийн гэрчилгээний урд талын зургийг оруулна уу'
                    )}
                {isCitizen
                  ? attachmentItem(
                      'idCardBack',
                      'Ард тал',
                      'Иргэний үнэмлэхний ард талын зургийг оруулна уу'
                    )
                  : attachmentItem(
                      'certificateBack',
                      'Ард тал',
                      'Улсын бүртгэлийн гэрчилгээний ард талын зургийг оруулна уу'
                    )}
              </Row>
            </Space>
          </SectionCard>
        )}

        <StepFooter disabled={!type} />
      </Space>
    </Form>
  );
};

export default Step1;
